import { Container, Grid, GridItem, TabList, Tabs, VStack } from "@chakra-ui/react";
import { Outlet, matchPath, useLocation } from "react-router";

import { useOfferingContext } from "../Offerings/OfferingContext";
import { useInvestmentContext } from "./InvestmentContext";
import { InvestmentHeaderCard } from "./shared/InvestmentHeaderCard";
import { InvestmentSidebarCards } from "./shared/InvestmentSidebarCards";
import { InvestmentAreaContainerSkeleton } from "./skeletons/InvestmentAreaContainerSkeleton";

import { useInvestmentSignatures } from "@equidefi/portals/hooks/useAgreements";
import { offeringHasAdditionalInfo } from "@equidefi/shared";
import { Tab } from "@equidefi/ui";
import { useMemo } from "react";

const pathPatternMatch = (path: string, pathname: string) => {
  const match = matchPath(`/offerings/:offeringId/investments/:id/${path}`, pathname);
  return match !== null;
};

export function InvestmentNavigation(): React.JSX.Element {
  const location = useLocation();
  const { offering } = useOfferingContext();

  const tabIndex = useMemo(() => {
    const list = [
      "summary",
      "investors",
      "payments",
      "kyc",
      "agreements",
      ["accreditation", "additional-info"],
      "history",
    ];

    const index = list.findIndex((path) => {
      if (Array.isArray(path)) {
        return path.some((p) => pathPatternMatch(p, location.pathname));
      }
      return pathPatternMatch(path, location.pathname);
    });

    return index === -1 ? 0 : index;
  }, [location]);

  if (!offering) return <></>;
  return (
    <Tabs mb={2} variant="equidefi" index={tabIndex}>
      <TabList>
        <Tab href={"./summary"}>Summary</Tab>
        <Tab href={"./investors"} state={{ refetch: true }}>
          Investors
        </Tab>
        <Tab href={"./payments"}>Payment</Tab>
        <Tab href={"./kyc"}>KYC</Tab>
        <Tab href={"./agreements"}>Agreement</Tab>
        {!offeringHasAdditionalInfo(offering) && <Tab href={"./accreditation"}>Accreditation</Tab>}
        {offeringHasAdditionalInfo(offering) && <Tab href={"./additional-info"}>Investor Data</Tab>}
        <Tab href={"./history"}>Activity Log</Tab>
      </TabList>
    </Tabs>
  );
}

export default function InvestmentAreaContainer(): React.JSX.Element {
  const { investment, isLoading } = useInvestmentContext();
  const { isLoading: isSignaturesLoading } = useInvestmentSignatures(investment?.id, {
    refetchInterval: closed ? 2_500 : false,
  });

  if (isLoading || isSignaturesLoading || !investment) {
    return <InvestmentAreaContainerSkeleton />;
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={4} align="stretch">
        <InvestmentHeaderCard investment={investment} />
        <InvestmentNavigation />
        <Grid gap="4" templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}>
          <GridItem colSpan={{ base: 1, md: 2 }}>
            <Outlet />
          </GridItem>

          <GridItem>
            <InvestmentSidebarCards investment={investment} />
          </GridItem>
        </Grid>
      </VStack>
    </Container>
  );
}
